import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { isDesktop } from 'react-device-detect'
import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import routePaths from './constants/routePaths'
import Home from './pages/Home'
import Welcome from './components/Welcome'
import MotifCounter from './components/MotifCounter'
import ExplanationFeature from './components/ExplanationFeature'
import TagFilter from './components/TagFilter'
import SideMenu from './components/SideMenu'
import MotifGenerator from './pages/MotifGenerator'
import WhiteOverlay from './components/WhiteOverlay'
import { useContextUI } from './contexts/UIContext'
import FullScreenLoading from './components/FullScreenLoading'
import GeographicFilter from './components/GeographicFilter'
import KeywordsFilter from './components/KeywordsFilter'
import ErrorMessage from './components/ErrorMessage'
import Snackbar from './components/Snackbar'
import { useContextSelectedMotifs } from './contexts/selectedMotifContext'
import BrandLogo from './components/BrandLogo'
import { useEnv } from './utils/useEnv'
import RotateDeviceNotification from './components/RotateDeviceNotification'
import ScreensaverContext from './contexts/screensaverContext'
import Gallery from './components/Gallery'
import AboutProject from './components/AboutProject'
import InteractionMotivatorContext from './contexts/interactionMotivatorContext'
import { UIElement } from './types/UIElements'
import LegalNotice from './components/LegalNotice'
import MotifDetails from './components/MotifDetails'
import MotifDetailsQueryHandler from './components/MotifDetailsQueryHandler'
import MotifDetailsCardHandler from './components/MotifDetailsCardHandler'
import { useContextMotifDetails } from './contexts/motifDetailsContext'
import FilterBar from './components/FilterBar'

const Router = () => (
  <BrowserRouter>
    <AppRoutes />
  </BrowserRouter>
)

const AppRoutes = () => {
  const { isInitialLoading, rotateDeviceNotificationVisible, elementsOpen, setElementsOpen } =
    useContextUI()
  const { isWeb, isKiosk } = useEnv()
  const { selectedMotifs } = useContextSelectedMotifs()
  const [showLoadingScreen, setShowLoadingScreen] = useState(false)
  const [isAnimationComplete, setIsAnimationComplete] = useState(false)
  const hasLoadedOnceRef = useRef(false)
  const { areDetailsOpen } = useContextMotifDetails()
  const location = useLocation()
  const firstSegment = useMemo(
    () => decodeURIComponent(location.pathname.split('/')[1] || '').toLowerCase(),
    [location.pathname],
  )
  const urlLocation = firstSegment

  const getElementToOpen = useCallback((): string => {
    const allowedRoutes: { [key: string]: string } = {
      about: 'ABOUT',
      gallery: 'GALLERY',
      how_it_works: 'EXPLANATION',
      explanation: 'EXPLANATION',
      legalnotice: 'LEGALNOTICE',
      tags: 'TAGFILTER',
      keywords: 'KEYWORDFILTER',
      location: 'GEOFILTER',
    }

    return allowedRoutes[urlLocation] ?? ''
  }, [urlLocation])

  const openElementCard = useCallback(
    (elementToOpen: string): void => {
      const elementMap: { [key: string]: UIElement } = {
        ABOUT: UIElement.ABOUT,
        LEGALNOTICE: UIElement.LEGALNOTICE,
        GALLERY: UIElement.GALLERY,
        EXPLANATION: UIElement.EXPLANATION,
        TAGFILTER: UIElement.TAGFILTER,
        KEYWORDFILTER: UIElement.KEYWORDFILTER,
        GEOFILTER: UIElement.GEOFILTER,
      }

      const uiElement = elementMap[elementToOpen]
      if (uiElement) setElementsOpen([uiElement])
    },
    [setElementsOpen],
  )

  const shouldStopLoadingAnimation = useMemo(() => {
    const element = getElementToOpen()
    const isCardRoute = firstSegment === 'card'
    const isCardOldRoute = firstSegment === 'card_old'
    const isIdRoute = /^\d/.test(firstSegment)

    return Boolean(element) || isCardRoute || isCardOldRoute || isIdRoute
  }, [firstSegment, getElementToOpen])

  useEffect(() => {
    const element = getElementToOpen()
    if (element) {
      openElementCard(element)
    }
  }, [getElementToOpen, openElementCard])

  const handleAnimationComplete = useCallback((forceDismiss?: boolean) => {
    setIsAnimationComplete(true)

    if (forceDismiss) {
      hasLoadedOnceRef.current = true
      setShowLoadingScreen(false)
    }
  }, [])

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    if (isInitialLoading && !showLoadingScreen && !hasLoadedOnceRef.current) {
      setShowLoadingScreen(true)
    }

    if (!isInitialLoading && showLoadingScreen && isAnimationComplete) {
      hasLoadedOnceRef.current = true
      timeoutId = setTimeout(() => {
        setShowLoadingScreen(false)
      }, 1000)
    }

    return () => timeoutId && clearTimeout(timeoutId)
  }, [isInitialLoading, showLoadingScreen, isAnimationComplete])

  const shouldShowWelcome = location.pathname === '/' || location.pathname === routePaths.GENERATE

  const isValidCardPath =
    /^\d/.test(firstSegment) || firstSegment === 'card' || firstSegment === 'card_old'

  const stopAnimationMinVisibleMs = useMemo(() => {
    const isCardRoute =
      firstSegment === 'card' || firstSegment === 'card_old' || /^\d/.test(firstSegment)
    return isCardRoute ? 7200 : 2000
  }, [firstSegment])

  return (
    <ScreensaverContext>
      {showLoadingScreen && (
        <FullScreenLoading
          isCompleted={!isInitialLoading}
          onAnimationComplete={handleAnimationComplete}
          stopAnimation={shouldStopLoadingAnimation}
          minVisibleMs={stopAnimationMinVisibleMs}
        />
      )}

      <InteractionMotivatorContext>
        <Routes>
          <Route path={routePaths.HOME} element={<Home canShowMotifMap={isAnimationComplete} />} />

          <Route
            path={routePaths.GENERATE}
            element={
              selectedMotifs.length < 1 ? <Navigate to={routePaths.HOME} /> : <MotifGenerator />
            }
          />

          <Route path={routePaths.ABOUT} element={<Home canShowMotifMap={isAnimationComplete} />} />

          <Route
            path={routePaths.GALLERY}
            element={<Home canShowMotifMap={isAnimationComplete} />}
          />

          <Route
            path={routePaths.EXPLANATION}
            element={<Home canShowMotifMap={isAnimationComplete} />}
          />

          <Route
            path={routePaths.LEGALNOTICE}
            element={<Home canShowMotifMap={isAnimationComplete} />}
          />

          <Route path={routePaths.TAGS} element={<Home canShowMotifMap={isAnimationComplete} />} />

          <Route
            path={routePaths.KEYWORDS}
            element={<Home canShowMotifMap={isAnimationComplete} />}
          />

          <Route
            path={routePaths.LOCATION}
            element={<Home canShowMotifMap={isAnimationComplete} />}
          />

          <Route
            path={routePaths.CARD_OLD}
            element={
              <>
                <Home canShowMotifMap={isAnimationComplete} />
                <MotifDetailsCardHandler />
              </>
            }
          />

          <Route
            path={routePaths.CARD}
            element={
              isValidCardPath ? (
                <>
                  <Home canShowMotifMap={isAnimationComplete} />
                  <MotifDetailsCardHandler />
                </>
              ) : (
                <Navigate to={routePaths.HOME} />
              )
            }
          />

          <Route path="*" element={<Navigate to={routePaths.HOME} />} />
        </Routes>

        <WhiteOverlay />
        <TagFilter />
        <KeywordsFilter />
        <GeographicFilter />
        {elementsOpen.includes(UIElement.GALLERY) && <Gallery />}
        {elementsOpen.includes(UIElement.ABOUT) && <AboutProject />}
        {elementsOpen.includes(UIElement.LEGALNOTICE) && <LegalNotice />}
        {areDetailsOpen && <MotifDetails />}
        <MotifDetailsQueryHandler />
        <MotifCounter />
        <FilterBar />
        <Snackbar />
        <ErrorMessage />
        {!isKiosk && isDesktop && isWeb && <BrandLogo />}
        {rotateDeviceNotificationVisible && <RotateDeviceNotification />}
        {shouldShowWelcome && <Welcome />}
        <ExplanationFeature />
        <SideMenu />
      </InteractionMotivatorContext>
    </ScreensaverContext>
  )
}

export default Router
